import { useState } from 'react'
import { Upload, FileText, CheckCircle, AlertCircle, Loader, X } from 'lucide-react'
import { apiRequest } from '@/config/api'

interface RecruiterResumeUploadProps {
  candidateId: string
  onUploadSuccess: (s3Key: string, originalFilename: string) => void
  onError: (error: string) => void
  className?: string
  disabled?: boolean
}

export default function RecruiterResumeUpload({ 
  candidateId,
  onUploadSuccess, 
  onError, 
  className = '',
  disabled = false
}: RecruiterResumeUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = ['.pdf', '.docx', '.doc']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
    
    if (!allowedTypes.includes(fileExtension)) {
      onError(`Unsupported file type. Please upload: ${allowedTypes.join(', ')}`)
      return
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      onError('File size too large. Maximum allowed size is 10MB.')
      return
    }

    setUploadedFile(file)
    uploadResume(file)
  }

  const uploadResume = async (file: File) => {
    setIsUploading(true)
    setUploadSuccess(false)

    try {
      // Step 1: Get presigned URL from the file-uploads API
      const presignedResponse = await apiRequest('file-uploads/resume/presigned-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          original_filename: file.name,
          file_size: file.size,
          mime_type: file.type,
          is_coverletter: false
        })
      })

      if (!presignedResponse.ok) {
        throw new Error('Failed to get upload URL')
      }

      const presignedData = await presignedResponse.json()
      
      if (!presignedData.success) {
        throw new Error(presignedData.message || 'Failed to get upload URL')
      }

      // Step 2: Upload file directly to S3 using presigned URL
      const formData = new FormData()
      
      // Add all the fields from the presigned response
      Object.keys(presignedData.fields).forEach(key => {
        formData.append(key, presignedData.fields[key])
      })
      
      // Add the file last
      formData.append('file', file)

      const s3Response = await fetch(presignedData.upload_url, {
        method: 'POST',
        body: formData
      })

      if (!s3Response.ok) {
        throw new Error('Failed to upload to S3')
      }

      // Step 3: Confirm upload with the API
      const confirmData = {
        s3_key: presignedData.s3_key,
        original_filename: file.name,
        file_size: file.size,
        mime_type: file.type,
        is_coverletter: false,
        is_loaded_by_recruiter: true,  // Mark as uploaded by recruiter
        candidate_uuid: candidateId     // Associate with the specific candidate
      }
      
      const confirmResponse = await apiRequest('file-uploads/resume/confirm-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(confirmData)
      })

      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm upload')
      }

      const confirmResult = await confirmResponse.json()
      
      if (confirmResult.success) {
        setUploadSuccess(true)
        onUploadSuccess(presignedData.s3_key, file.name)
      } else {
        throw new Error(confirmResult.message || 'Upload confirmation failed')
      }

    } catch (error: any) {
      console.error('Upload error:', error)
      onError(error.message || 'Failed to upload resume. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    if (disabled) return
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const clearFile = () => {
    setUploadedFile(null)
    setUploadSuccess(false)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
    // Clear the input to allow re-selecting the same file
    e.target.value = ''
  }

  return (
    <div className={`recruiter-resume-upload ${className}`}>
      {!uploadedFile ? (
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
            disabled 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : isDragOver 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400 cursor-pointer'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="flex flex-col items-center space-y-2">
            <Upload className={`w-6 h-6 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
            <div>
              <p className={`text-sm font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
                Upload new resume for candidate
              </p>
              <p className={`text-xs ${disabled ? 'text-gray-300' : 'text-gray-500'} mt-1`}>
                Supports PDF, DOCX, DOC files (max 10MB)
              </p>
            </div>
            <label className={`cursor-pointer px-3 py-2 rounded text-sm font-medium transition-colors ${
              disabled 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}>
              Choose File
              <input
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={handleFileInput}
                disabled={disabled}
                className="hidden"
              />
            </label>
          </div>
        </div>
      ) : (
        <div className="border rounded-lg p-3 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {uploadedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {Math.round(uploadedFile.size / 1024)} KB
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {isUploading && (
                <div className="flex items-center space-x-1">
                  <Loader className="w-4 h-4 animate-spin text-blue-600" />
                  <span className="text-xs text-blue-600">Uploading...</span>
                </div>
              )}
              
              {uploadSuccess && (
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-green-600">Uploaded</span>
                </div>
              )}
              
              <button
                onClick={clearFile}
                className="text-gray-400 hover:text-gray-600"
                disabled={isUploading}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {uploadSuccess && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-xs text-green-700">
                ✓ Resume uploaded successfully and will be updated in candidate profile
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}